import { creatorbase } from '@round/api';
import moment from 'moment';
import { useCreatorbaseTimeSeries, useTiktokAudios } from '@round/creatorbase-helpers/api-hooks';
import { useTiktokAudioAssets } from '@round/platforms/tiktok';

type Params = {
    songId?: number | null;
    page?: number;
    page_size?: number;
    ordering?: creatorbase.GetTiktokAudiosOrderingParam;
};

export default function useTiktokTopAudios({ songId, ...rest }: Params) {
    const today = moment();
    const twoWeeksAgo = moment().subtract(14, 'days');

    const {
        data: audiosData,
        isLoading,
        isFetching,
        isError,
        error,
        errorMessage,
    } = useTiktokAudios(
        { ...rest, song_id: songId ?? 0 },
        {
            enabled: typeof songId === 'number',
            // preserve count on pagination/ordering changes
            placeholderData: (previousData) => (previousData ? { ...previousData, results: [] } : undefined),
        }
    );

    const audioIds = audiosData?.results.map((audio) => audio.id) ?? [];

    const { data: timeSeries, isLoading: isTimeSeriesLoading } = useCreatorbaseTimeSeries(
        {
            type: 'tiktok_audio',
            fields: ['posts_daily_change'],
            ids: audioIds,
            start_date: twoWeeksAgo.format('YYYY-MM-DD'),
            end_date: today.format('YYYY-MM-DD'),
        },
        { enabled: audioIds.length > 0 }
    );

    const { data: assets, isLoading: isAssetsLoading } = useTiktokAudioAssets(audioIds, {
        enabled: audioIds.length > 0,
    });

    const data = audiosData
        ? {
              audios: audiosData,
              timeSeries: timeSeries ?? null,
              assets: assets ?? [],
          }
        : null;

    return {
        data,
        isLoading: isLoading || isTimeSeriesLoading || isAssetsLoading,
        isFetching,
        isError,
        error,
        errorMessage,
    };
}
